import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { eventService } from '../../services/eventService';
import { registrationService } from '../../services/registrationService';
import { useBrand } from '../../brands/BrandProvider';
import { useBrandRegistrationCopy } from '../../brands/registrationCopy';

export type DuplicateEventSource = {
  id: string;
  title: string;
  slug: string;
};

function slugifyEventPath(value: string): string {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '')
    .replace(/--+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

function suggestSlugFromSource(source: DuplicateEventSource): string {
  const base = slugifyEventPath(source.slug || source.title) || 'evento';
  const withCopy = `${base}-copia`.slice(0, 80).replace(/-+$/g, '');
  return withCopy.length >= 3 ? withCopy : `${base}-2`;
}

type SlugStatus = 'idle' | 'checking' | 'available' | 'taken' | 'invalid';

type Props = {
  open: boolean;
  source: DuplicateEventSource | null;
  onClose: () => void;
  onDuplicated: (newEventId: string) => void;
};

const DuplicateEventDialog: React.FC<Props> = ({ open, source, onClose, onDuplicated }) => {
  const brand = useBrand();
  const t = useBrandRegistrationCopy();
  const noun = brand.key === 'zuadaria' ? 'show' : 'evento';

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [slugStatus, setSlugStatus] = useState<SlugStatus>('idle');
  const [slugMessage, setSlugMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !source) return;
    setTitle(`${source.title} (cópia)`.slice(0, 200));
    setSlug(suggestSlugFromSource(source));
    setSlugTouched(false);
    setSlugStatus('idle');
    setSlugMessage(null);
    setError(null);
    setSubmitting(false);
  }, [open, source]);

  useEffect(() => {
    if (!open) return;
    const normalized = slugifyEventPath(slug);
    if (normalized !== slug) {
      setSlug(normalized);
      return;
    }

    if (!normalized) {
      setSlugStatus('invalid');
      setSlugMessage('Informe um identificador para o link.');
      return;
    }
    if (normalized.length < 3) {
      setSlugStatus('invalid');
      setSlugMessage('Mínimo de 3 caracteres.');
      return;
    }
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(normalized)) {
      setSlugStatus('invalid');
      setSlugMessage('Use só letras minúsculas, números e hífens.');
      return;
    }

    let cancelled = false;
    setSlugStatus('checking');
    setSlugMessage('Verificando disponibilidade…');
    const timer = window.setTimeout(async () => {
      try {
        const { available } = await registrationService.checkSlugAvailability(normalized);
        if (cancelled) return;
        if (available) {
          setSlugStatus('available');
          setSlugMessage('Identificador disponível.');
        } else {
          setSlugStatus('taken');
          setSlugMessage('Este identificador já está em uso. Escolha outro.');
        }
      } catch {
        if (cancelled) return;
        setSlugStatus('invalid');
        setSlugMessage('Não foi possível verificar o identificador. Tente de novo.');
      }
    }, 450);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [open, slug]);

  const publicPreview = useMemo(() => {
    const origin =
      typeof window !== 'undefined' ? window.location.origin.replace(/\/$/, '') : '';
    return slug ? `${origin}/${slug}` : '';
  }, [slug]);

  const canSubmit =
    !!source &&
    title.trim().length >= 2 &&
    slugStatus === 'available' &&
    !submitting;

  const handleTitleChange = (value: string) => {
    setTitle(value);
    if (!slugTouched) {
      const next = slugifyEventPath(value);
      if (next) setSlug(next.length >= 3 ? next : suggestSlugFromSource(source!));
    }
  };

  const handleDuplicate = async () => {
    if (!source || !canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const response = await eventService.duplicateEvent(source.id, {
        slug,
        title: title.trim(),
      });
      if (!response.success || !response.data) {
        setError(response.message || `Não foi possível duplicar o ${noun}.`);
        return;
      }
      const newId = String((response.data as { _id?: string; id?: string })._id || (response.data as any).id || '');
      if (!newId) {
        setError('Cópia criada, mas não foi possível abrir a edição.');
        return;
      }
      onDuplicated(newId);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || `Erro ao duplicar o ${noun}.`);
    } finally {
      setSubmitting(false);
    }
  };

  const slugAdornment =
    slugStatus === 'checking' ? (
      <CircularProgress size={16} />
    ) : slugStatus === 'available' ? (
      <CheckCircleOutlineIcon color="success" fontSize="small" />
    ) : slugStatus === 'taken' || slugStatus === 'invalid' ? (
      <ErrorOutlineIcon color="error" fontSize="small" />
    ) : null;

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 2,
          m: { xs: 1.5, sm: 'auto' },
          width: { xs: 'calc(100% - 24px)', sm: undefined },
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, fontWeight: 700 }}>
        Duplicar {noun}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Vamos criar uma cópia de{' '}
          <Box component="span" fontWeight={700} color="text.primary">
            {source?.title}
          </Box>
          . Ingressos, formulário e configurações vêm junto. Inscrições e pagamentos{' '}
          <strong>não</strong> são copiados.
        </Typography>

        <TextField
          label="Novo título"
          value={title}
          onChange={(e) => handleTitleChange(e.target.value)}
          fullWidth
          autoFocus
          disabled={submitting}
          InputLabelProps={{ shrink: true }}
          sx={{ mb: 2 }}
        />

        <TextField
          label="Novo link (slug)"
          value={slug}
          onChange={(e) => {
            setSlugTouched(true);
            setSlug(e.target.value);
          }}
          fullWidth
          disabled={submitting}
          error={slugStatus === 'taken' || slugStatus === 'invalid'}
          helperText={slugMessage || t('Este texto entra na URL pública do evento.')}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: slugAdornment ? (
              <InputAdornment position="end">{slugAdornment}</InputAdornment>
            ) : undefined,
          }}
          sx={{ mb: 1 }}
        />

        {publicPreview ? (
          <Box
            sx={{
              mb: 2,
              px: 1.5,
              py: 1,
              borderRadius: 1.5,
              bgcolor: 'action.hover',
              wordBreak: 'break-all',
            }}
          >
            <Typography variant="caption" color="text.secondary" display="block">
              Prévia do link
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {publicPreview}
            </Typography>
          </Box>
        ) : null}

        {error ? (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          pb: 2.5,
          pt: 0,
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          gap: 1,
        }}
      >
        <Button fullWidth={false} onClick={onClose} disabled={submitting} sx={{ minHeight: 44 }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleDuplicate()}
          disabled={!canSubmit}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <ContentCopyIcon />}
          sx={{ minHeight: 44, fontWeight: 700, textTransform: 'none', width: { xs: '100%', sm: 'auto' } }}
        >
          {submitting ? 'Duplicando…' : `Duplicar ${noun}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DuplicateEventDialog;
